import L from "leaflet";
import * as turf from "@turf/turf";
import * as toGeoJSON from "@tmcw/togeojson";
import JSZip from "jszip";
import DxfParser from "dxf-parser";
import shp from "shpjs";

export const lineworkTool = {
  id: "linework",
  name: "Linework",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy(); this._rt = null; },
  renderPanel(app, containerEl) { if (!this._rt) this._rt = createRuntime(app); this._rt.mount(containerEl); },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) { if (!this._rt) this._rt = createRuntime(app); this._rt.restore?.(data); },
  async handleExternalFiles(app, files) {
    if (!this._rt) this._rt = createRuntime(app);
    return this._rt.importExternalFiles?.(files);
  },
};

function createRuntime(app) {
  const map = app.map.instance;
  const mapEl = app.map.el;
  const root = L.featureGroup().addTo(map);
  const layers = [];
  let activeId = null;
  let container = null;
  let drawSession = null;
  let editingLayerId = null;
  let seq = 1;
  let inGlobalSync = false;
  let pendingSiblingLookup = null;
  let importStatus = "Ready.";

  function uid(prefix) {
    seq += 1;
    return `${prefix}_${Date.now().toString(36)}_${seq.toString(36)}`;
  }

  function esc(s) {
    return String(s ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));
  }

  function setImportStatus(message) {
    importStatus = String(message || "").trim() || "Ready.";
    const el = container?.querySelector?.("#lw-import-status");
    if (el) el.textContent = importStatus;
  }

  function byId(id) {
    return layers.find((x) => x.id === id) || null;
  }

  function selectedLayer() {
    return byId(activeId);
  }

  function ensureSelectedLayer({ createIfMissing = true } = {}) {
    let layer = selectedLayer();
    if (layer) return layer;
    if (layers.length) {
      activeId = layers[0].id;
      return layers[0];
    }
    if (!createIfMissing) return null;
    layer = createLayer({ name: `Layer ${layers.length + 1}`, fc: { type: "FeatureCollection", features: [] }, visible: true });
    activeId = layer.id;
    render();
    return layer;
  }

  function featureStyle(feature) {
    const t = String(feature?.geometry?.type || "");
    if (t.includes("Polygon")) return { color: "#22d3ee", weight: 2, fillOpacity: 0.16 };
    if (t.includes("Point")) return { radius: 5, color: "#f59e0b", fillColor: "#f59e0b", fillOpacity: 0.85, weight: 1 };
    return { color: "#60a5fa", weight: 3 };
  }

  function renderLayerGeometry(layer) {
    layer.group.clearLayers();
    const gj = L.geoJSON(layer.fc, {
      style: (feature) => ({ ...featureStyle(feature), color: layer.color }),
      pointToLayer: (feature, latlng) => L.circleMarker(latlng, { ...featureStyle(feature), color: layer.color, fillColor: layer.color }),
    });
    gj.eachLayer((l) => layer.group.addLayer(l));
    // Add arrowheads for features authored as arrow lines.
    (layer.fc?.features || []).forEach((f) => {
      if ((f?.properties?.draw_mode || "") !== "arrowline") return;
      const coords = f?.geometry?.type === "LineString" ? f.geometry.coordinates : null;
      if (!Array.isArray(coords) || coords.length < 2) return;
      const a = coords[coords.length - 2];
      const b = coords[coords.length - 1];
      if (!Array.isArray(a) || !Array.isArray(b)) return;
      const aLL = L.latLng(Number(a[1]), Number(a[0]));
      const bLL = L.latLng(Number(b[1]), Number(b[0]));
      const aPt = map.latLngToLayerPoint(aLL);
      const bPt = map.latLngToLayerPoint(bLL);
      const dx = Number(bPt.x) - Number(aPt.x);
      const dy = Number(bPt.y) - Number(aPt.y);
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);
      const marker = L.marker([Number(b[1]), Number(b[0])], {
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
          className: "lw-arrowhead",
          html: `<span style="transform: rotate(${angle}deg);">▶</span>`,
        }),
      });
      layer.group.addLayer(marker);
    });
  }

  function applyLayerOpacity(layer, opacity01) {
    const o = Math.max(0, Math.min(1, Number(opacity01) || 0));
    layer.opacity = o;
    layer.group.eachLayer((l) => {
      if (l?.setStyle) {
        try { l.setStyle({ opacity: o, fillOpacity: Math.max(0.04, o * 0.28) }); } catch {}
      } else if (l?.setOpacity) {
        try { l.setOpacity(o); } catch {}
      }
    });
  }

  function syncGlobalLayer(rec) {
    if (!rec?.globalLayerId) return;
    if (inGlobalSync) return;
    app.services.layers.updateLayer(rec.globalLayerId, {
      name: rec.name,
      visible: rec.visible,
      opacity: rec.opacity ?? 1,
      meta: { featureCount: rec.fc?.features?.length || 0 },
    });
  }

  function removeLocalLayer(id, { fromGlobal = false } = {}) {
    const i = layers.findIndex((x) => x.id === id);
    if (i === -1) return;
    const rec = layers[i];
    try { map.removeLayer(rec.group); } catch {}
    try { root.removeLayer(rec.group); } catch {}
    layers.splice(i, 1);
    if (activeId === id) activeId = layers[0]?.id || null;
    if (!fromGlobal && rec.globalLayerId) {
      inGlobalSync = true;
      try { app.services.layers.removeLayer(rec.globalLayerId); } finally { inGlobalSync = false; }
    }
    render();
  }

  function registerGlobalLayer(rec) {
    if (!rec || rec.globalLayerId) return;
    rec.opacity = Number.isFinite(Number(rec.opacity)) ? Number(rec.opacity) : 1;
    rec.globalLayerId = app.services.layers.addLayer("linework", {
      name: rec.name,
      kind: "overlay",
      group: "Linework",
      type: "managed",
      visible: rec.visible,
      opacity: rec.opacity,
      volatile: true,
      meta: { featureCount: rec.fc?.features?.length || 0 },
      managed: {
        setVisible(v) {
          rec.visible = !!v;
          if (rec.visible) { root.addLayer(rec.group); rec.group.addTo(map); }
          else { map.removeLayer(rec.group); root.removeLayer(rec.group); }
          if (!inGlobalSync) render();
        },
        setOpacity(o) {
          applyLayerOpacity(rec, o);
          if (!inGlobalSync) render();
        },
        getBounds() {
          const b = rec.group?.getBounds?.();
          return b?.isValid?.() ? b : null;
        },
        bringToFront() {
          try { rec.group?.bringToFront?.(); } catch {}
        },
        remove() {
          if (inGlobalSync) return;
          inGlobalSync = true;
          try { removeLocalLayer(rec.id, { fromGlobal: true }); } finally { inGlobalSync = false; }
        },
      },
    });
  }

  function createLayer({ name, fc, visible = true, color = "#60a5fa", id = uid("lw") }) {
    const group = L.featureGroup();
    const rec = {
      id,
      name: name || `Layer ${layers.length + 1}`,
      fc: normalizeFeatureCollection(fc),
      visible: !!visible,
      color,
      opacity: 1,
      group,
    };
    renderLayerGeometry(rec);
    applyLayerOpacity(rec, rec.opacity);
    if (rec.visible) {
      root.addLayer(group);
      group.addTo(map);
    }
    layers.unshift(rec);
    registerGlobalLayer(rec);
    syncGlobalLayer(rec);
    if (!activeId) activeId = rec.id;
    return rec;
  }

  function normalizeFeatureCollection(data) {
    if (!data || typeof data !== "object") return { type: "FeatureCollection", features: [] };
    if (data.type === "FeatureCollection" && Array.isArray(data.features)) return JSON.parse(JSON.stringify(data));
    if (data.type === "Feature" && data.geometry) return { type: "FeatureCollection", features: [JSON.parse(JSON.stringify(data))] };
    if (data.type && data.coordinates) return { type: "FeatureCollection", features: [{ type: "Feature", properties: {}, geometry: JSON.parse(JSON.stringify(data)) }] };
    return { type: "FeatureCollection", features: [] };
  }

  function featureCollectionBounds(fc) {
    try {
      const b = L.geoJSON(fc).getBounds();
      return b?.isValid?.() ? b : null;
    } catch {
      return null;
    }
  }

  function childEls(el, localName) {
    return Array.from(el?.children || []).filter((node) => String(node?.localName || "").toLowerCase() === String(localName || "").toLowerCase());
  }

  function firstChildText(el, localName) {
    const node = childEls(el, localName)[0];
    return String(node?.textContent || "").trim();
  }

  function parseExtendedData(placemarkEl) {
    const out = {};
    childEls(placemarkEl, "ExtendedData").forEach((extEl) => {
      childEls(extEl, "Data").forEach((dataEl) => {
        const key = String(dataEl.getAttribute("name") || "").trim();
        const value = firstChildText(dataEl, "value");
        if (key) out[key] = value;
      });
    });
    return out;
  }

  function buildPlacemarkQuery(rec) {
    const props = rec?.properties || {};
    const direct = String(props.address || "").trim();
    if (direct) return direct;
    return [
      props.name,
      props.city,
      props.state,
      props.country,
    ].filter(Boolean).join(", ");
  }

  function buildPlacemarkQueryVariants(rec) {
    const props = rec?.properties || {};
    const uniq = new Set();
    const add = (value) => {
      const text = String(value || "").replace(/\(\d+\)/g, "").replace(/\s+/g, " ").trim();
      if (text) uniq.add(text);
    };
    add(props.address);
    add([props.name, props.city, props.state, props.country].filter(Boolean).join(", "));
    add([props.name, props.city, props.state].filter(Boolean).join(", "));
    add([props.name, props.city].filter(Boolean).join(", "));
    add([props.name, props.state].filter(Boolean).join(", "));
    return [...uniq];
  }

  async function geocodeFreeform(queryOrQueries) {
    const queries = Array.isArray(queryOrQueries) ? queryOrQueries : [queryOrQueries];
    for (const raw of queries) {
      const q = String(raw || "").trim();
      if (!q) continue;
      const encoded = encodeURIComponent(q);
      try {
        const photon = await fetch(`https://photon.komoot.io/api/?q=${encoded}&limit=1`, {
          headers: { Accept: "application/json" },
        });
        if (photon.ok) {
          const data = await photon.json();
          const ft = Array.isArray(data?.features) ? data.features[0] : null;
          const coords = ft?.geometry?.coordinates;
          const lon = Number(Array.isArray(coords) ? coords[0] : NaN);
          const lat = Number(Array.isArray(coords) ? coords[1] : NaN);
          if (Number.isFinite(lat) && Number.isFinite(lon)) return { lat, lon, source: "photon", query: q };
        }
      } catch {}
      try {
        const nominatim = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encoded}`, {
          headers: { Accept: "application/json" },
        });
        if (nominatim.ok) {
          const rows = await nominatim.json();
          const row = Array.isArray(rows) ? rows[0] : null;
          const lat = Number(row?.lat);
          const lon = Number(row?.lon);
          if (Number.isFinite(lat) && Number.isFinite(lon)) return { lat, lon, source: "nominatim", query: q };
        }
      } catch {}
    }
    return null;
  }

  function extractGeometrylessPlacemarks(dom) {
    const placemarkEls = Array.from(dom?.getElementsByTagName?.("Placemark") || []);
    return placemarkEls.map((placemarkEl) => {
      const properties = {
        name: firstChildText(placemarkEl, "name"),
        address: firstChildText(placemarkEl, "address"),
        description: firstChildText(placemarkEl, "description"),
        ...parseExtendedData(placemarkEl),
      };
      if (!properties.city && properties.City) properties.city = properties.City;
      if (!properties.state && properties.State) properties.state = properties.State;
      if (!properties.country && properties.Country) properties.country = properties.Country;
      return {
        properties,
        query: buildPlacemarkQuery({ properties }),
        queries: buildPlacemarkQueryVariants({ properties }),
      };
    }).filter((rec) => rec.query || rec.queries.length);
  }

  async function geocodeGeometrylessPlacemarks(name, dom) {
    const placemarks = extractGeometrylessPlacemarks(dom);
    if (!placemarks.length) {
      setImportStatus(`No coordinates or usable address fields found in ${name}.`);
      return {
        name: stripKnownArchiveSuffix(name),
        featureCollection: { type: "FeatureCollection", features: [] },
        warnings: ["This KML/KMZ has placemarks but no coordinates, and no usable address/name fields were found."],
      };
    }
    setImportStatus(`No geometry in ${name}. Geocoding ${placemarks.length} placemarks...`);
    app.ui.toast(`This KML/KMZ has ${placemarks.length} placemarks but no coordinates. Geocoding approximate points...`);
    const cache = new Map();
    const features = [];
    let misses = 0;
    let processed = 0;
    const workerCount = Math.min(8, Math.max(2, Math.ceil(placemarks.length / 20)));
    let cursor = 0;
    const runNext = async () => {
      const idx = cursor;
      cursor += 1;
      if (idx >= placemarks.length) return;
      const rec = placemarks[idx];
      const key = (rec.queries[0] || rec.query || "").toLowerCase();
      let hit = cache.get(key);
      if (hit === undefined) {
        hit = await geocodeFreeform(rec.queries.length ? rec.queries : rec.query);
        cache.set(key, hit || null);
      }
      if (!hit) {
        misses += 1;
      } else {
        features.push({
          type: "Feature",
          properties: {
            ...rec.properties,
            source: "kml-geocode-fallback",
            geocode_query: hit.query || rec.query,
            geocode_source: hit.source,
          },
          geometry: {
            type: "Point",
            coordinates: [hit.lon, hit.lat],
          },
        });
      }
      processed += 1;
      if (processed === placemarks.length || processed % 10 === 0) {
        setImportStatus(`Geocoding ${name}: ${processed}/${placemarks.length} processed, ${features.length} located, ${misses} unresolved...`);
      }
      if (cursor < placemarks.length) await runNext();
    };
    await Promise.all(Array.from({ length: workerCount }, () => runNext()));
    const warnings = [
      `This KML/KMZ had ${placemarks.length} placemark${placemarks.length === 1 ? "" : "s"} but no coordinates.`,
      features.length
        ? `HydroTools geocoded ${features.length} placemark${features.length === 1 ? "" : "s"} from address/name fields.${misses ? ` ${misses} could not be located.` : ""}`
        : "HydroTools could not geocode any placemarks from the available address/name fields.",
    ];
    return {
      name: stripKnownArchiveSuffix(name),
      featureCollection: { type: "FeatureCollection", features },
      warnings,
    };
  }

  async function parseKmlLike(name, text) {
    setImportStatus(`Parsing ${name}...`);
    app.ui.toast(`Parsing ${name}...`);
    const dom = new DOMParser().parseFromString(text, "text/xml");
    const parseErrors = dom?.getElementsByTagName?.("parsererror");
    if (parseErrors?.length) {
      throw new Error(`XML parse failed for ${name}`);
    }
    const featureCollection = normalizeFeatureCollection(toGeoJSON.kml(dom));
    const hasGeometry = (featureCollection.features || []).some((feature) => feature?.geometry?.type);
    if (hasGeometry) {
      setImportStatus(`Parsed ${name}: ${(featureCollection.features || []).length} drawable feature(s) found.`);
      app.ui.toast(`Parsed ${name}: found ${(featureCollection.features || []).length} feature(s) with geometry.`);
      return [{ name: stripKnownArchiveSuffix(name), featureCollection, warnings: [] }];
    }
    setImportStatus(`Parsed ${name}: no geometry found. Attempting geocoding...`);
    app.ui.toast(`Parsed ${name}: no geometry found, attempting placemark geocoding.`);
    return [await geocodeGeometrylessPlacemarks(name, dom)];
  }

  function sourceCRSOptions(selected = "auto") {
    const built = app.services.projections.listCRS()
      .map((c) => `<option value="${esc(c.code)}" ${String(selected).toUpperCase() === c.code.toUpperCase() ? "selected" : ""}>${esc(c.code)} - ${esc(c.name)}</option>`)
      .join("");
    return `<option value="auto" ${selected === "auto" ? "selected" : ""}>Auto (from file / WGS84)</option>${built}`;
  }

  function render() {
    if (!container) return;
    const rows = layers.map((l) => {
      const active = l.id === activeId;
      const count = l.fc?.features?.length || 0;
      return `
        <div class="layer-row ${active ? "lw-active" : ""}" data-layer-row="${l.id}">
          <label class="layer-check" title="Visible">
            <input type="checkbox" data-act="vis" data-id="${l.id}" ${l.visible ? "checked" : ""}>
          </label>
          <div class="layer-meta">
            <div class="layer-head">
              ${editingLayerId === l.id
                ? `<input class="lw-inline-edit" data-act="rename-input" data-id="${l.id}" value="${esc(l.name)}" />`
                : `<div class="layer-name lw-name" data-act="rename" data-id="${l.id}" title="Double click to rename">${esc(l.name)}</div>`}
              <div class="small" title="Feature count">${count}</div>
            </div>
          </div>
          <div class="lw-actions">
            <input class="lw-color" type="color" value="${esc(l.color)}" data-act="color" data-id="${l.id}" title="Layer color">
            <button class="iconbtn" data-act="zoom" data-id="${l.id}" title="Zoom to layer">⌖</button>
            <select data-act="fmt" data-id="${l.id}" class="lw-fmt">
            <option value="geojson">GeoJSON</option>
            <option value="kml">KML</option>
            <option value="gpx">GPX</option>
            <option value="dxf">DXF</option>
            </select>
            <button class="iconbtn" data-act="exp" data-id="${l.id}" title="Export layer">↓</button>
            <button class="iconbtn danger" data-act="del" data-id="${l.id}" title="Delete layer">x</button>
          </div>
        </div>
      `;
    }).join("");

    container.innerHTML = `
      <div class="card">
        <div style="font-weight:700;">Import Vector Files</div>
        <div style="height:8px;"></div>
        <div class="row" style="flex-wrap:wrap;">
          <select id="lw-import-crs" style="min-width:240px;">${sourceCRSOptions("auto")}</select>
          <button id="lw-import-btn" class="btn primary">Import files</button>
          <input id="lw-file" type="file" accept=".geojson,.json,.kml,.kmz,.gpx,.dxf,.zip,.shp,.dbf,.prj,.cpg" multiple style="display:none;">
          <input id="lw-folder" type="file" webkitdirectory directory multiple style="display:none;">
        </div>
        <div class="small" style="margin-top:6px;">Supported: GeoJSON, KML, KMZ, GPX, DXF, SHP ZIP, SHP + DBF/PRJ/CPG. If a SHP is picked alone, the tool will ask for its folder to find siblings.</div>
        <div id="lw-import-status" class="small" style="margin-top:8px;">${esc(importStatus)}</div>
      </div>

      <div style="height:10px;"></div>

      <div class="card">
        <div style="font-weight:700;">Create / Draw</div>
        <div style="height:8px;"></div>
        <div class="row" style="flex-wrap:wrap;">
          <button id="lw-new-layer" class="iconbtn" title="New layer">+</button>
          <button id="lw-draw-point" class="iconbtn" title="Draw point">•</button>
          <button id="lw-draw-line" class="iconbtn" title="Draw line">／</button>
          <button id="lw-draw-arrow" class="iconbtn" title="Draw arrowed line">➤</button>
          <button id="lw-draw-freehand" class="iconbtn" title="Draw freehand">∿</button>
          <button id="lw-draw-rect" class="iconbtn" title="Draw rectangle">▭</button>
          <button id="lw-draw-poly" class="iconbtn" title="Draw polygon">▱</button>
          <button id="lw-draw-circle" class="iconbtn" title="Draw circle">◯</button>
        </div>
        <div class="small" style="margin-top:6px;">Draw goes into the selected layer below.</div>
      </div>

      <div style="height:10px;"></div>
      ${rows || `<div class="small">No linework layers yet.</div>`}
    `;
  }

  function addFeatureToSelected(feature) {
    const layer = ensureSelectedLayer({ createIfMissing: true });
    if (!layer) {
      app.ui.toast("Select or create a layer first");
      return;
    }
    layer.fc.features.push(feature);
    renderLayerGeometry(layer);
    syncGlobalLayer(layer);
    render();
  }

  function stopDraw(reason = "stopped") {
    if (!drawSession) return;
    drawSession.off?.();
    drawSession.cleanup?.();
    drawSession = null;
    mapEl.style.cursor = "";
    app.ui.toast(`Draw ${reason}`);
  }

  function startLineOrPolygon(mode) {
    const layer = ensureSelectedLayer({ createIfMissing: true });
    if (!layer) {
      app.ui.toast("Select a layer first");
      return;
    }
    stopDraw("replaced");
    mapEl.style.cursor = "crosshair";
    const points = [];
    const tempLine = L.polyline([], { color: "#22d3ee", weight: 2, dashArray: "6 6" }).addTo(map);
    const tempPoly = mode === "polygon" ? L.polygon([], { color: "#22d3ee", weight: 2, fillOpacity: 0.08 }) : null;
    if (tempPoly) tempPoly.addTo(map);
    const dblWas = map.doubleClickZoom?.enabled?.() ?? false;
    map.doubleClickZoom?.disable?.();

    const update = (cursor) => {
      const path = cursor ? [...points, cursor] : [...points];
      tempLine.setLatLngs(path);
      if (tempPoly && path.length >= 2) tempPoly.setLatLngs(path);
    };
    const onClick = (e) => { points.push(e.latlng); update(); };
    const onMove = (e) => update(e.latlng);
    const finish = () => {
      const minPts = mode === "polygon" ? 3 : 2;
      if (points.length < minPts) {
        app.ui.toast(`Need at least ${minPts} points`);
        return;
      }
      const coords = points.map((p) => [p.lng, p.lat]);
      const geometry = mode === "polygon"
        ? { type: "Polygon", coordinates: [[...coords, coords[0]]] }
        : { type: "LineString", coordinates: coords };
      addFeatureToSelected({ type: "Feature", properties: { source: "draw", draw_mode: mode }, geometry });
      stopDraw("completed");
    };
    const onDbl = (e) => { L.DomEvent.stop(e); finish(); };
    const onCtx = (e) => { L.DomEvent.stop(e); finish(); };
    const onKey = (e) => { if (e.key === "Escape") stopDraw("cancelled"); };

    map.on("click", onClick);
    map.on("mousemove", onMove);
    map.on("dblclick", onDbl);
    map.on("contextmenu", onCtx);
    document.addEventListener("keydown", onKey);

    drawSession = {
      off() {
        map.off("click", onClick);
        map.off("mousemove", onMove);
        map.off("dblclick", onDbl);
        map.off("contextmenu", onCtx);
        document.removeEventListener("keydown", onKey);
        if (dblWas) setTimeout(() => map.doubleClickZoom?.enable?.(), 0);
      },
      cleanup() {
        try { map.removeLayer(tempLine); } catch {}
        if (tempPoly) { try { map.removeLayer(tempPoly); } catch {} }
      },
    };
    app.ui.toast(`Drawing ${mode}. Click to add vertices, double-click/right-click to finish.`);
  }

  function startCircle() {
    const layer = ensureSelectedLayer({ createIfMissing: true });
    if (!layer) {
      app.ui.toast("Select a layer first");
      return;
    }
    stopDraw("replaced");
    mapEl.style.cursor = "crosshair";
    const dblWas = map.doubleClickZoom?.enabled?.() ?? false;
    map.doubleClickZoom?.disable?.();

    let center = null;
    let temp = null;
    const onClick = (e) => {
      if (!center) {
        center = e.latlng;
        temp = L.circle(center, { radius: 1, color: "#22d3ee", weight: 2, fillOpacity: 0.08 }).addTo(map);
        return;
      }
      const radiusM = center.distanceTo(e.latlng);
      if (!(radiusM > 0)) {
        app.ui.toast("Circle radius must be > 0");
        return;
      }
      const circle = turf.circle([center.lng, center.lat], radiusM / 1000, { units: "kilometers", steps: 64 });
      addFeatureToSelected({ type: "Feature", properties: { source: "draw", draw_mode: "circle", radius_m: radiusM }, geometry: circle.geometry });
      stopDraw("completed");
    };
    const onMove = (e) => {
      if (!center || !temp) return;
      const radiusM = center.distanceTo(e.latlng);
      temp.setRadius(Math.max(0, radiusM));
    };
    const onKey = (e) => { if (e.key === "Escape") stopDraw("cancelled"); };

    map.on("click", onClick);
    map.on("mousemove", onMove);
    document.addEventListener("keydown", onKey);

    drawSession = {
      off() {
        map.off("click", onClick);
        map.off("mousemove", onMove);
        document.removeEventListener("keydown", onKey);
        if (dblWas) setTimeout(() => map.doubleClickZoom?.enable?.(), 0);
      },
      cleanup() { if (temp) { try { map.removeLayer(temp); } catch {} } },
    };
    app.ui.toast("Drawing circle. Click center, then click edge to finish.");
  }

  function startPoint() {
    const layer = ensureSelectedLayer({ createIfMissing: true });
    if (!layer) {
      app.ui.toast("Select a layer first");
      return;
    }
    stopDraw("replaced");
    mapEl.style.cursor = "crosshair";
    const onClick = (e) => {
      addFeatureToSelected({
        type: "Feature",
        properties: { source: "draw", draw_mode: "point" },
        geometry: { type: "Point", coordinates: [e.latlng.lng, e.latlng.lat] },
      });
      stopDraw("completed");
    };
    const onKey = (e) => { if (e.key === "Escape") stopDraw("cancelled"); };
    map.on("click", onClick);
    document.addEventListener("keydown", onKey);
    drawSession = {
      off() {
        map.off("click", onClick);
        document.removeEventListener("keydown", onKey);
      },
      cleanup() {},
    };
    app.ui.toast("Drawing point. Click once to place.");
  }

  function startRectangle() {
    const layer = ensureSelectedLayer({ createIfMissing: true });
    if (!layer) {
      app.ui.toast("Select a layer first");
      return;
    }
    stopDraw("replaced");
    mapEl.style.cursor = "crosshair";
    const dblWas = map.doubleClickZoom?.enabled?.() ?? false;
    map.doubleClickZoom?.disable?.();
    let a = null;
    let temp = null;
    const rectCoords = (p1, p2) => ([
      [p1.lng, p1.lat],
      [p2.lng, p1.lat],
      [p2.lng, p2.lat],
      [p1.lng, p2.lat],
      [p1.lng, p1.lat],
    ]);
    const onClick = (e) => {
      if (!a) {
        a = e.latlng;
        temp = L.polygon([], { color: "#22d3ee", weight: 2, fillOpacity: 0.08 }).addTo(map);
        return;
      }
      const ring = rectCoords(a, e.latlng);
      addFeatureToSelected({
        type: "Feature",
        properties: { source: "draw", draw_mode: "rectangle" },
        geometry: { type: "Polygon", coordinates: [ring] },
      });
      stopDraw("completed");
    };
    const onMove = (e) => {
      if (!a || !temp) return;
      temp.setLatLngs(rectCoords(a, e.latlng).map((xy) => [xy[1], xy[0]]));
    };
    const onKey = (e) => { if (e.key === "Escape") stopDraw("cancelled"); };
    map.on("click", onClick);
    map.on("mousemove", onMove);
    document.addEventListener("keydown", onKey);
    drawSession = {
      off() {
        map.off("click", onClick);
        map.off("mousemove", onMove);
        document.removeEventListener("keydown", onKey);
        if (dblWas) setTimeout(() => map.doubleClickZoom?.enable?.(), 0);
      },
      cleanup() { if (temp) { try { map.removeLayer(temp); } catch {} } },
    };
    app.ui.toast("Drawing rectangle. Click first corner, then opposite corner.");
  }

  function startFreehand() {
    const layer = ensureSelectedLayer({ createIfMissing: true });
    if (!layer) {
      app.ui.toast("Select a layer first");
      return;
    }
    stopDraw("replaced");
    mapEl.style.cursor = "crosshair";
    const dragWas = map.dragging?.enabled?.() ?? false;
    map.dragging?.disable?.();
    const pts = [];
    let drawing = false;
    const temp = L.polyline([], { color: "#22d3ee", weight: 2, dashArray: "3 3" }).addTo(map);
    const onDown = (e) => {
      drawing = true;
      pts.length = 0;
      pts.push(e.latlng);
      temp.setLatLngs(pts);
    };
    const onMove = (e) => {
      if (!drawing) return;
      pts.push(e.latlng);
      temp.setLatLngs(pts);
    };
    const onUp = () => {
      if (!drawing) return;
      drawing = false;
      if (pts.length < 2) {
        app.ui.toast("Need at least 2 points");
        return;
      }
      addFeatureToSelected({
        type: "Feature",
        properties: { source: "draw", draw_mode: "freehand" },
        geometry: { type: "LineString", coordinates: pts.map((p) => [p.lng, p.lat]) },
      });
      stopDraw("completed");
    };
    const onKey = (e) => { if (e.key === "Escape") stopDraw("cancelled"); };
    map.on("mousedown", onDown);
    map.on("mousemove", onMove);
    map.on("mouseup", onUp);
    document.addEventListener("keydown", onKey);
    drawSession = {
      off() {
        map.off("mousedown", onDown);
        map.off("mousemove", onMove);
        map.off("mouseup", onUp);
        document.removeEventListener("keydown", onKey);
        if (dragWas) map.dragging?.enable?.();
      },
      cleanup() { try { map.removeLayer(temp); } catch {} },
    };
    app.ui.toast("Drawing freehand. Hold mouse button and drag, release to finish.");
  }

  function withEpsg4326(fc) {
    const out = normalizeFeatureCollection(fc);
    out.crs = { type: "name", properties: { name: "EPSG:4326" } };
    return out;
  }

  function stripKnownArchiveSuffix(name) {
    return String(name || "").replace(/\.(geojson|json|kml|kmz|gpx|dxf|zip|shp|dbf|prj|cpg)$/i, "");
  }

  function getShapefileKey(file) {
    const rel = String(file?.webkitRelativePath || "").replace(/\\/g, "/");
    if (rel) return stripKnownArchiveSuffix(rel);
    return stripKnownArchiveSuffix(file?.name || "");
  }

  async function parseShapefileZip(file) {
    const buff = await file.arrayBuffer();
    const parsed = await shp(buff);
    const items = Array.isArray(parsed) ? parsed : [parsed];
    const baseName = stripKnownArchiveSuffix(file.name) || "shapefile";
    return items.map((fc, index) => ({
      name: fc?.fileName || (items.length > 1 ? `${baseName}_${index + 1}` : baseName),
      featureCollection: withEpsg4326(fc),
    }));
  }

  async function parseFile(file) {
    const name = file.name || "import";
    const lower = name.toLowerCase();
    if (lower.endsWith(".geojson") || lower.endsWith(".json")) {
      const text = await file.text();
      return [{ name: stripKnownArchiveSuffix(name), featureCollection: normalizeFeatureCollection(JSON.parse(text)) }];
    }
    if (lower.endsWith(".kml")) {
      const text = await file.text();
      return parseKmlLike(name, text);
    }
    if (lower.endsWith(".gpx")) {
      const text = await file.text();
      const dom = new DOMParser().parseFromString(text, "text/xml");
      return [{ name: stripKnownArchiveSuffix(name), featureCollection: normalizeFeatureCollection(toGeoJSON.gpx(dom)) }];
    }
    if (lower.endsWith(".kmz")) {
      const buff = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(buff);
      const kmlEntry = Object.values(zip.files).find((f) => /\.kml$/i.test(f.name));
      if (!kmlEntry) throw new Error("KMZ has no KML");
      const kmlText = await kmlEntry.async("string");
      return parseKmlLike(name, kmlText);
    }
    if (lower.endsWith(".zip")) {
      return parseShapefileZip(file);
    }
    if (lower.endsWith(".dxf")) {
      const text = await file.text();
      const parser = new DxfParser();
      const dxf = parser.parseSync(text);
      return [{ name: stripKnownArchiveSuffix(name), featureCollection: dxfToFeatureCollection(dxf) }];
    }
    throw new Error(`Unsupported format: ${name}`);
  }

  async function parseShapefileGroup(group) {
    if (!group?.shp) throw new Error("Missing .shp file");
    const payload = {
      shp: await group.shp.arrayBuffer(),
    };
    if (group.dbf) payload.dbf = await group.dbf.arrayBuffer();
    if (group.prj) payload.prj = await group.prj.arrayBuffer();
    if (group.cpg) payload.cpg = await group.cpg.arrayBuffer();
    const parsed = await shp(payload);
    return [{
      name: stripKnownArchiveSuffix(group.shp.name) || "shapefile",
      featureCollection: withEpsg4326(parsed),
    }];
  }

  function collectImportJobs(fileList) {
    const files = [...(fileList || [])];
    const jobs = [];
    const shpGroups = new Map();

    files.forEach((file) => {
      const lower = String(file?.name || "").toLowerCase();
      const match = lower.match(/^(.*)\.(shp|dbf|prj|cpg)$/i);
      if (!match) {
        jobs.push({ kind: "file", file });
        return;
      }
      const base = match[1];
      const ext = match[2].toLowerCase();
      const group = shpGroups.get(base) || {};
      group[ext] = file;
      shpGroups.set(base, group);
    });

    shpGroups.forEach((group) => {
      if (group.shp) jobs.push({ kind: "shapefile", group });
    });

    return jobs;
  }

  function needsSiblingLookup(fileList) {
    const files = [...(fileList || [])];
    const groups = new Map();
    let hasStandaloneShp = false;

    files.forEach((file) => {
      const lower = String(file?.name || "").toLowerCase();
      const match = lower.match(/^(.*)\.(shp|dbf|prj|cpg)$/i);
      if (!match) return;
      const key = getShapefileKey(file);
      const ext = match[2].toLowerCase();
      const group = groups.get(key) || {};
      group[ext] = file;
      groups.set(key, group);
      if (ext === "shp" && !file?.webkitRelativePath) hasStandaloneShp = true;
    });

    if (!hasStandaloneShp) return false;
    return [...groups.values()].some((group) => group.shp && (!group.dbf || !group.prj));
  }

  function mergeSiblingFiles(primaryFiles, folderFiles) {
    const merged = [...(primaryFiles || [])];
    const existing = new Set(merged);
    const folderGroups = new Map();
    const primaryGroups = new Map();

    [...(folderFiles || [])].forEach((file) => {
      const lower = String(file?.name || "").toLowerCase();
      const match = lower.match(/^(.*)\.(shp|dbf|prj|cpg)$/i);
      if (!match) return;
      const group = folderGroups.get(stripKnownArchiveSuffix(file?.name || "")) || {};
      group[match[2].toLowerCase()] = file;
      folderGroups.set(stripKnownArchiveSuffix(file?.name || ""), group);
    });

    [...(primaryFiles || [])].forEach((file) => {
      const lower = String(file?.name || "").toLowerCase();
      const match = lower.match(/^(.*)\.(shp|dbf|prj|cpg)$/i);
      if (!match) return;
      const key = stripKnownArchiveSuffix(file?.name || "");
      const group = primaryGroups.get(key) || {};
      group[match[2].toLowerCase()] = file;
      primaryGroups.set(key, group);
    });

    primaryGroups.forEach((group, key) => {
      if (!group.shp) return;
      const siblings = folderGroups.get(key);
      if (!siblings) return;
      ["dbf", "prj", "cpg"].forEach((ext) => {
        if (group[ext] || !siblings[ext] || existing.has(siblings[ext])) return;
        merged.push(siblings[ext]);
        existing.add(siblings[ext]);
      });
    });

    return merged;
  }

  async function beginImportSelection(fileList) {
    const files = [...(fileList || [])];
    if (!files.length) return { imported: 0 };
    if (needsSiblingLookup(files)) {
      pendingSiblingLookup = files;
      setImportStatus("Waiting for SHP sibling files from selected folder...");
      app.ui.toast("Select the folder containing the SHP so Linework can attach DBF/PRJ/CPG files.");
      container?.querySelector?.("#lw-folder")?.click();
      return { imported: 0, pendingSiblingLookup: true };
    }
    pendingSiblingLookup = null;
    return importFiles(files);
  }

  function dxfToFeatureCollection(dxf) {
    const features = [];
    const entities = Array.isArray(dxf?.entities) ? dxf.entities : [];

    const toCoord = (v) => [Number(v?.x || 0), Number(v?.y || 0)];
    const addLine = (a, b, props = {}) => {
      features.push({ type: "Feature", properties: props, geometry: { type: "LineString", coordinates: [toCoord(a), toCoord(b)] } });
    };

    entities.forEach((e) => {
      const type = String(e?.type || "").toUpperCase();
      if (type === "LINE" && e.vertices?.length >= 2) {
        addLine(e.vertices[0], e.vertices[1], { source: "dxf", entity: "LINE", layer: e.layer || "" });
        return;
      }
      if ((type === "POLYLINE" || type === "LWPOLYLINE") && Array.isArray(e.vertices) && e.vertices.length >= 2) {
        const coords = e.vertices.map((v) => toCoord(v));
        const closed = !!(e.shape || e.closed);
        if (closed) {
          features.push({ type: "Feature", properties: { source: "dxf", entity: type, layer: e.layer || "" }, geometry: { type: "Polygon", coordinates: [[...coords, coords[0]]] } });
        } else {
          features.push({ type: "Feature", properties: { source: "dxf", entity: type, layer: e.layer || "" }, geometry: { type: "LineString", coordinates: coords } });
        }
        return;
      }
      if (type === "CIRCLE" && e.center && Number.isFinite(Number(e.radius))) {
        const poly = turf.circle([Number(e.center.x), Number(e.center.y)], Number(e.radius), { units: "meters", steps: 64 });
        features.push({ type: "Feature", properties: { source: "dxf", entity: "CIRCLE", layer: e.layer || "", radius: e.radius }, geometry: poly.geometry });
      }
    });

    return { type: "FeatureCollection", features };
  }

  async function importFiles(fileList) {
    const jobs = collectImportJobs(fileList);
    if (!jobs.length) return { imported: 0 };
    const srcSel = container?.querySelector?.("#lw-import-crs")?.value || "auto";
    const importedBounds = [];
    let importedCount = 0;
    setImportStatus(`Starting import of ${jobs.length} file${jobs.length === 1 ? "" : "s"}...`);
    app.ui.toast(`Starting import of ${jobs.length} file${jobs.length === 1 ? "" : "s"}...`);

    for (const job of jobs) {
      try {
        const label = job.kind === "shapefile"
          ? (job.group?.shp?.name || "shapefile")
          : (job.file?.name || "file");
        setImportStatus(`Importing ${label}...`);
        const parsedItems = job.kind === "shapefile"
          ? await parseShapefileGroup(job.group)
          : await parseFile(job.file);
        const importedNames = [];
        parsedItems.forEach((item) => {
          const norm = app.services.io.normalizeImportFeatureCollection(item.featureCollection, {
            sourceCRS: srcSel === "auto" ? null : srcSel,
            targetCRS: "EPSG:4326",
          });
          const featureCount = Array.isArray(norm?.featureCollection?.features) ? norm.featureCollection.features.length : 0;
          (Array.isArray(item.warnings) ? item.warnings : []).forEach((warning) => app.ui.toast(warning));
          if (!featureCount) {
            setImportStatus(`No drawable features found in ${item.name}.`);
            app.ui.toast(`No drawable features found in ${item.name}`);
            return;
          }
          createLayer({ name: item.name, fc: norm.featureCollection, visible: true });
          const bounds = featureCollectionBounds(norm.featureCollection);
          if (bounds) importedBounds.push(bounds);
          importedNames.push(`${item.name}${norm.transformed ? ` (${norm.sourceCRS} -> ${norm.targetCRS})` : ""}`);
          importedCount += 1;
        });
        activeId = layers[0]?.id || activeId;
        if (importedNames.length) {
          setImportStatus(`Imported ${importedNames.join(", ")}.`);
          app.ui.toast(`Imported ${importedNames.join(", ")}`);
        }
      } catch (err) {
        const label = job.kind === "shapefile"
          ? (job.group?.shp?.name || "shapefile")
          : (job.file?.name || "file");
        console.error("[Linework] import failed", label, err);
        setImportStatus(`Import failed for ${label}: ${err?.message || "unknown error"}`);
        app.ui.toast(`Import failed: ${label}${err?.message ? ` (${err.message})` : ""}`);
      }
    }
    if (importedBounds.length) {
      const merged = importedBounds[0];
      importedBounds.slice(1).forEach((bounds) => merged.extend(bounds));
      if (merged?.isValid?.()) map.fitBounds(merged.pad(0.15));
    }
    if (!importedCount) setImportStatus("Import finished with no drawable output.");
    render();
    return { imported: importedCount };
  }

  async function importExternalFiles(fileList) {
    const files = [...(fileList || [])].filter(Boolean);
    if (!files.length) return { imported: 0 };
    setImportStatus(`Received ${files.length} external file${files.length === 1 ? "" : "s"} for import...`);
    app.ui.toast(`Received ${files.length} dropped file${files.length === 1 ? "" : "s"} for Linework import.`);
    return beginImportSelection(files);
  }

  function projectExportTarget(format) {
    const fmt = String(format || "geojson").toLowerCase();
    if (fmt === "gpx" || fmt === "kml") return "EPSG:4326";
    return app.services.projections.getProjectProfile()?.horizontalCRS || "EPSG:4326";
  }

  function prepareExportFC(layer, format) {
    const targetCRS = projectExportTarget(format);
    const prepared = app.services.io.prepareExportFeatureCollection(layer.fc, {
      sourceCRS: "EPSG:4326",
      targetCRS,
      includeLegacyCrs: format === "geojson",
    });
    return prepared;
  }

  function download(filename, content, mime) {
    const blob = new Blob([content], { type: mime });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(u); a.remove(); }, 250);
  }

  function exportGeoJSON(fc, name, targetCRS) {
    const out = {
      ...fc,
      metadata: { export_crs: targetCRS, exported_at: new Date().toISOString() },
    };
    download(`${name}.geojson`, JSON.stringify(out, null, 2), "application/geo+json");
  }

  function kmlCoordString(coords) {
    return (coords || [])
      .filter((xy) => Array.isArray(xy) && Number.isFinite(Number(xy[0])) && Number.isFinite(Number(xy[1])))
      .map((xy) => `${xy[0]},${xy[1]},0`)
      .join(" ");
  }

  function kmlGeometryMarkup(geometry) {
    const g = geometry;
    if (!g?.type) return "";

    if (g.type === "LineString") {
      const coords = kmlCoordString(g.coordinates);
      return coords ? `<LineString><coordinates>${coords}</coordinates></LineString>` : "";
    }

    if (g.type === "Polygon") {
      const rings = Array.isArray(g.coordinates) ? g.coordinates : [];
      const outer = kmlCoordString(rings[0]);
      if (!outer) return "";
      const inner = rings.slice(1)
        .map((ring) => kmlCoordString(ring))
        .filter(Boolean)
        .map((coords) => `<innerBoundaryIs><LinearRing><coordinates>${coords}</coordinates></LinearRing></innerBoundaryIs>`)
        .join("");
      return `<Polygon><outerBoundaryIs><LinearRing><coordinates>${outer}</coordinates></LinearRing></outerBoundaryIs>${inner}</Polygon>`;
    }

    if (g.type === "Point") {
      const [x, y] = g.coordinates || [];
      if (!Number.isFinite(Number(x)) || !Number.isFinite(Number(y))) return "";
      return `<Point><coordinates>${x},${y},0</coordinates></Point>`;
    }

    if (g.type === "MultiLineString") {
      const parts = (g.coordinates || [])
        .map((line) => kmlGeometryMarkup({ type: "LineString", coordinates: line }))
        .filter(Boolean);
      if (!parts.length) return "";
      return parts.length === 1 ? parts[0] : `<MultiGeometry>${parts.join("")}</MultiGeometry>`;
    }

    if (g.type === "MultiPolygon") {
      const parts = (g.coordinates || [])
        .map((poly) => kmlGeometryMarkup({ type: "Polygon", coordinates: poly }))
        .filter(Boolean);
      if (!parts.length) return "";
      return parts.length === 1 ? parts[0] : `<MultiGeometry>${parts.join("")}</MultiGeometry>`;
    }

    if (g.type === "MultiPoint") {
      const parts = (g.coordinates || [])
        .map((point) => kmlGeometryMarkup({ type: "Point", coordinates: point }))
        .filter(Boolean);
      if (!parts.length) return "";
      return parts.length === 1 ? parts[0] : `<MultiGeometry>${parts.join("")}</MultiGeometry>`;
    }

    if (g.type === "GeometryCollection") {
      const parts = (g.geometries || []).map((part) => kmlGeometryMarkup(part)).filter(Boolean);
      if (!parts.length) return "";
      return parts.length === 1 ? parts[0] : `<MultiGeometry>${parts.join("")}</MultiGeometry>`;
    }

    return "";
  }

  function exportKML(fc, name) {
    const placemarks = [];
    const features = fc?.features || [];
    features.forEach((f, i) => {
      const g = f?.geometry;
      if (!g) return;
      const nm = esc(f?.properties?.name || `${name}_${i + 1}`);
      const geometryMarkup = kmlGeometryMarkup(g);
      if (!geometryMarkup) return;
      placemarks.push(`<Placemark><name>${nm}</name>${geometryMarkup}</Placemark>`);
    });
    const body = `<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2"><Document><name>${esc(name)}</name>${placemarks.join("")}</Document></kml>\n`;
    download(`${name}.kml`, body, "application/vnd.google-earth.kml+xml");
  }

  function exportGPX(fc, name) {
    const routes = [];
    const wpts = [];
    (fc?.features || []).forEach((f, i) => {
      const g = f?.geometry;
      const nm = esc(f?.properties?.name || `${name}_${i + 1}`);
      if (!g) return;
      if (g.type === "LineString") {
        const pts = (g.coordinates || [])
          .filter((xy) => Array.isArray(xy) && Number.isFinite(Number(xy[0])) && Number.isFinite(Number(xy[1])))
          .map((xy) => `<rtept lat="${Number(xy[1]).toFixed(8)}" lon="${Number(xy[0]).toFixed(8)}"></rtept>`)
          .join("");
        if (pts) routes.push(`<rte><name>${nm}</name>${pts}</rte>`);
      }
      if (g.type === "Point") {
        const [x, y] = g.coordinates || [];
        if (Number.isFinite(Number(x)) && Number.isFinite(Number(y))) wpts.push(`<wpt lat="${Number(y).toFixed(8)}" lon="${Number(x).toFixed(8)}"><name>${nm}</name></wpt>`);
      }
    });
    const body = `<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" creator="HydroTools" xmlns="http://www.topografix.com/GPX/1/1"><metadata><name>${esc(name)}</name><time>${new Date().toISOString()}</time></metadata>${wpts.join("")}${routes.join("")}</gpx>\n`;
    download(`${name}.gpx`, body, "application/gpx+xml");
  }

  function exportDXF(fc, name) {
    const out = ["0", "SECTION", "2", "ENTITIES"];
    (fc?.features || []).forEach((f) => {
      const g = f?.geometry;
      if (!g) return;
      if (g.type === "LineString") {
        const pts = g.coordinates || [];
        if (pts.length < 2) return;
        out.push("0", "POLYLINE", "8", "LINEWORK", "66", "1", "70", "0");
        pts.forEach((xy) => out.push("0", "VERTEX", "8", "LINEWORK", "10", `${Number(xy[0]).toFixed(8)}`, "20", `${Number(xy[1]).toFixed(8)}`, "30", "0.0"));
        out.push("0", "SEQEND");
      }
      if (g.type === "Polygon") {
        const ring = g.coordinates?.[0] || [];
        if (ring.length < 4) return;
        out.push("0", "POLYLINE", "8", "LINEWORK", "66", "1", "70", "1");
        ring.forEach((xy) => out.push("0", "VERTEX", "8", "LINEWORK", "10", `${Number(xy[0]).toFixed(8)}`, "20", `${Number(xy[1]).toFixed(8)}`, "30", "0.0"));
        out.push("0", "SEQEND");
      }
    });
    out.push("0", "ENDSEC", "0", "EOF");
    download(`${name}.dxf`, `${out.join("\n")}\n`, "application/dxf");
  }

  function exportLayer(id, fmt) {
    const layer = byId(id);
    if (!layer) return;
    try {
      const prepared = prepareExportFC(layer, fmt);
      if (fmt === "kml") exportKML(prepared.featureCollection, layer.name);
      else if (fmt === "gpx") exportGPX(prepared.featureCollection, layer.name);
      else if (fmt === "dxf") exportDXF(prepared.featureCollection, layer.name);
      else exportGeoJSON(prepared.featureCollection, layer.name, prepared.targetCRS);
      app.ui.toast(`Exported ${layer.name} as ${fmt.toUpperCase()}`);
    } catch (err) {
      console.error("[Linework] export failed", err);
      app.ui.toast(`Export failed: ${layer.name}`);
    }
  }

  function zoomToLayer(id) {
    const layer = byId(id);
    if (!layer) return;
    const gj = L.geoJSON(layer.fc);
    const b = gj.getBounds();
    if (b?.isValid?.()) map.fitBounds(b.pad(0.15));
  }

  function onClick(e) {
    const actEl = e.target?.closest?.("[data-act]");
    if (actEl) {
      const act = actEl.dataset.act;
      const id = actEl.dataset.id;
      if (act === "exp" && id) {
        const fmtEl = container.querySelector(`select[data-act="fmt"][data-id="${id}"]`);
        exportLayer(id, fmtEl?.value || "geojson");
        return;
      }
      if (act === "del" && id) {
        removeLocalLayer(id, { fromGlobal: false });
        return;
      }
      if (act === "zoom" && id) {
        zoomToLayer(id);
        return;
      }
    }

    if (e.target?.id === "lw-import-btn") {
      container.querySelector("#lw-file")?.click();
      return;
    }
    if (e.target?.id === "lw-new-layer") {
      createLayer({ name: `Layer ${layers.length + 1}`, fc: { type: "FeatureCollection", features: [] }, visible: true });
      activeId = layers[0]?.id || activeId;
      render();
      return;
    }
    if (e.target?.id === "lw-draw-point") { startPoint(); return; }
    if (e.target?.id === "lw-draw-line") { startLineOrPolygon("line"); return; }
    if (e.target?.id === "lw-draw-arrow") { startLineOrPolygon("arrowline"); return; }
    if (e.target?.id === "lw-draw-freehand") { startFreehand(); return; }
    if (e.target?.id === "lw-draw-rect") { startRectangle(); return; }
    if (e.target?.id === "lw-draw-poly") { startLineOrPolygon("polygon"); return; }
    if (e.target?.id === "lw-draw-circle") { startCircle(); return; }

    const row = e.target?.closest?.("[data-layer-row]");
    if (row) {
      if (e.target?.closest?.('[data-act="rename"]') || e.target?.closest?.('[data-act="rename-input"]')) return;
      const tag = String(e.target?.tagName || "").toLowerCase();
      if (["input", "select", "button", "label"].includes(tag)) return;
      activeId = row.dataset.layerRow;
      render();
    }
  }

  function onDblClick(e) {
    const nameEl = e.target?.closest?.('[data-act="rename"]');
    if (!nameEl) return;
    const id = nameEl.dataset.id;
    editingLayerId = id;
    render();
    setTimeout(() => {
      const input = container?.querySelector?.(`[data-act="rename-input"][data-id="${id}"]`);
      input?.focus?.();
      input?.select?.();
    }, 0);
  }

  function commitInlineRename(id, raw, cancel = false) {
    const layer = byId(id);
    if (layer && !cancel) {
      const next = String(raw || "").trim();
      if (next) layer.name = next;
      syncGlobalLayer(layer);
    }
    editingLayerId = null;
    render();
  }

  function onKeyDown(e) {
    const input = e.target?.closest?.('[data-act="rename-input"]');
    if (!input) return;
    const id = input.dataset.id;
    if (!id) return;
    if (e.key === "Enter") {
      e.preventDefault();
      commitInlineRename(id, input.value, false);
    } else if (e.key === "Escape") {
      e.preventDefault();
      commitInlineRename(id, input.defaultValue, true);
    }
  }

  function onFocusOut(e) {
    const input = e.target?.closest?.('[data-act="rename-input"]');
    if (!input) return;
    const id = input.dataset.id;
    if (!id) return;
    commitInlineRename(id, input.value, false);
  }

  function onChange(e) {
    const t = e.target;
    if (t?.id === "lw-file") {
      beginImportSelection(t.files);
      t.value = "";
      return;
    }
    if (t?.id === "lw-folder") {
      const merged = mergeSiblingFiles(pendingSiblingLookup, t.files);
      pendingSiblingLookup = null;
      importFiles(merged);
      t.value = "";
      return;
    }
    const act = t?.dataset?.act;
    const id = t?.dataset?.id;
    if (!act || !id) return;
    const layer = byId(id);
    if (!layer) return;
    if (act === "vis") {
      if (!layer.globalLayerId) {
        layer.visible = !!t.checked;
        if (layer.visible) { root.addLayer(layer.group); layer.group.addTo(map); }
        else { map.removeLayer(layer.group); root.removeLayer(layer.group); }
        render();
        return;
      }
      inGlobalSync = true;
      try { app.services.layers.setVisible(layer.globalLayerId, !!t.checked); } finally { inGlobalSync = false; }
      syncGlobalLayer(layer);
      return;
    }
    if (act === "color") {
      layer.color = String(t.value || "#60a5fa").toLowerCase();
      renderLayerGeometry(layer);
      applyLayerOpacity(layer, layer.opacity ?? 1);
      syncGlobalLayer(layer);
      return;
    }
  }

  function mount(next) {
    container = next;
    render();
    container.addEventListener("click", onClick);
    container.addEventListener("change", onChange);
    container.addEventListener("dblclick", onDblClick);
    container.addEventListener("keydown", onKeyDown);
    container.addEventListener("focusout", onFocusOut);
  }

  function snapshot() {
    return {
      layers: layers.map((l) => ({
        id: l.id,
        name: l.name,
        fc: l.fc,
        visible: l.visible,
        color: l.color,
      })),
      activeId,
    };
  }

  function restore(data) {
    if (!data || typeof data !== "object") return;
    stopDraw("restore");
    layers.splice(0).forEach((l) => {
      if (l.globalLayerId) {
        inGlobalSync = true;
        try { app.services.layers.removeLayer(l.globalLayerId); } catch {}
        finally { inGlobalSync = false; }
      }
      try { map.removeLayer(l.group); } catch {}
      try { root.removeLayer(l.group); } catch {}
    });
    (Array.isArray(data.layers) ? data.layers : []).forEach((l) => {
      createLayer({
        id: l.id,
        name: l.name,
        fc: normalizeFeatureCollection(l.fc),
        visible: l.visible !== false,
        color: l.color || "#60a5fa",
      });
    });
    activeId = data.activeId || layers[0]?.id || null;
    render();
  }

  function destroy() {
    stopDraw("destroy");
    if (container) {
      container.removeEventListener("click", onClick);
      container.removeEventListener("change", onChange);
      container.removeEventListener("dblclick", onDblClick);
      container.removeEventListener("keydown", onKeyDown);
      container.removeEventListener("focusout", onFocusOut);
    }
    layers.splice(0).forEach((l) => {
      if (l.globalLayerId) {
        inGlobalSync = true;
        try { app.services.layers.removeLayer(l.globalLayerId); } catch {}
        finally { inGlobalSync = false; }
      }
      try { map.removeLayer(l.group); } catch {}
      try { root.removeLayer(l.group); } catch {}
    });
    root.clearLayers();
    map.removeLayer(root);
    container = null;
  }

  return { mount, destroy, snapshot, restore, importExternalFiles };
}
